/**
 * El cronómetro.
 *
 * **Número y anillo, no solo anillo** (principio IV): un arco que se vacía comunica por forma y por
 * color, pero no dice cuánto queda a quien lo necesita en cifras. Y el anillo no se anima con una
 * transición: se dibuja en la posición que toca, así que `prefers-reduced-motion` no tiene nada que
 * suprimir.
 */

import { useTranslation } from 'react-i18next';
import type { ClockReading } from '../game/use-clock.js';

const RADIO = 44;
const PERIMETRO = 2 * Math.PI * RADIO;

export function Cronometro({
  reading,
  frozen,
  size = 100,
}: {
  reading: ClockReading;
  frozen: 'paused' | 'hostAway' | null;
  size?: number;
}): React.JSX.Element {
  const { t } = useTranslation();

  if (reading.seconds === null) {
    return (
      <p role="status" className="text-text-dim text-lg font-semibold">
        {frozen === null ? t('game.question.timeUp') : t(`game.${frozen}.title`)}
      </p>
    );
  }

  const fraction = reading.fraction ?? 0;
  // Por debajo de cinco segundos cambia también la forma —el trazo engorda—, no solo el color.
  const urgente = reading.seconds <= 5;

  return (
    <div
      className="relative inline-flex items-center justify-center"
      style={{ width: size, height: size }}
    >
      <svg viewBox="0 0 100 100" width={size} height={size} aria-hidden="true" focusable="false">
        <circle
          cx="50"
          cy="50"
          r={RADIO}
          fill="none"
          stroke="currentColor"
          strokeWidth={urgente ? 10 : 6}
          // Sólido y no un alfa: con transparencia el gris depende de lo que se pinte debajo
          // y nadie lo compone a mano. Es la pista de la carrera, no el corredor.
          className="text-surface-hi"
        />
        <circle
          cx="50"
          cy="50"
          r={RADIO}
          fill="none"
          stroke="currentColor"
          strokeWidth={urgente ? 10 : 6}
          strokeLinecap="round"
          strokeDasharray={PERIMETRO}
          strokeDashoffset={PERIMETRO * (1 - fraction)}
          transform="rotate(-90 50 50)"
          /*
           * **El arco interpola; el número no.**
           *
           * React repinta una vez por segundo, que es lo barato y lo correcto para la cifra. Pero
           * el arco saltando de segundo en segundo se lee como un reloj roto. Con la transición,
           * el navegador interpola entre las dos posiciones que le llegan y el arco baja seguido,
           * sin que React pinte un solo fotograma de más.
           *
           * `linear` a propósito y sin excepción: un reloj que acelera es un reloj que miente.
           */
          style={{ transition: 'stroke-dashoffset var(--dur-tick) linear' }}
          // El acento no puede pintar un trazo: sobre papel da 1,16:1. Lo que cambia cuando
          // quedan pocos segundos son tres cosas a la vez —color, grosor y el número—, no una.
          className={urgente ? 'text-ko' : 'text-edge'}
        />
      </svg>
      <span
        // `polite` y no `assertive`: un cronómetro que interrumpiera cada segundo haría inservible
        // el lector de pantalla justo cuando hay que leer la pregunta.
        role="timer"
        aria-live="off"
        // La cifra escala con la caja. Eran 24 px fijos, y la misma pieza se usa a 72 px en el
        // móvil y a 110 en el proyector: proyectados, esos 24 px no se leen desde el fondo del
        // aula, en una pantalla donde el enunciado llega a 84.
        style={{ fontSize: Math.round(size * 0.36) }}
        className="font-display absolute font-bold tabular-nums"
      >
        {reading.seconds}
      </span>
      <span className="sr-only">{t('game.question.timeLeft', { seconds: reading.seconds })}</span>
    </div>
  );
}
